import React, { Fragment } from "react";

import ViewSection from "./ViewSection";
import { faCube, faIdCardAlt, faKey, faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { TitledContent } from "./TitledContent";
import { IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Renderer } from "../descriptors/common";
import ResourceFormButton from "./ResourceFormButton";
import client from "../descriptors/client";
import DeleteResourceButton from "./DeleteResourceButton";
import ClientRoleMappingRuleDialog from "../utils/ClientRoleMappingRuleDialog";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import NotSetValue from "./NotSetValue";
import { isInternalClient } from "../tools";
import { userTranslations } from "./UserView";

type DataField = {
    name: string;
    label: MessageDescriptor;
};

const apiAccessField: DataField = {
    name: "api_access",
    label: defineMessage({
        id: "client.api_access",
        defaultMessage: "/api access with the secret",
    }),
};

const RuleButton: React.FC<{
    ruleId?: number;
    action: "create" | "update";
    roles: any[];
}> = ({ ruleId, action, roles }) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const revalidator = useRevalidator();

    return (
        <>
            <IconButton
                onClick={() => {
                    setDialogOpen(true);
                }}
                sx={
                    action === "create"
                        ? {
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              "&:hover": {
                                  bgcolor: "primary.dark",
                              },
                          }
                        : {
                              color: "primary.main",
                          }
                }
            >
                <FontAwesomeIcon icon={action == "create" ? faPlus : faPencil} />
            </IconButton>
            <ClientRoleMappingRuleDialog
                ruleId={ruleId}
                roles={roles}
                open={dialogOpen}
                onHide={(changed) => {
                    if (changed) revalidator.revalidate();
                    setDialogOpen(false);
                }}
            />
        </>
    );
};

const BasicTable: React.FC<{
    data: any;
    fields: DataField[];
    renderers: Record<string, Renderer>;
    fieldNameKeyFactory: (key: string) => string;
}> = ({ data, fields, renderers, fieldNameKeyFactory }) => {
    const rows = [];
    for (const field of fields) {
        const { name, label } = field;

        let value = data[name];
        if (name in renderers) value = renderers[name](value, data);
        const nameKey = fieldNameKeyFactory(name);
        rows.push(
            <Fragment key={nameKey}>
                <Grid size={4}>
                    <FormattedMessage {...label} />
                </Grid>
                <Grid size={8}>{value}</Grid>
            </Fragment>,
        );
    }

    return (
        <Grid container spacing={2}>
            {...rows}
        </Grid>
    );
};

const ContextSubstitutions: React.FC<{ data: any }> = ({ data }) => {
    const rows = [];
    for (const row of data) {
        for (const field in row.substitution) {
            rows.push(
                <Fragment key={field}>
                    <Grid size={4}>
                        {/* eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id */}
                        <FormattedMessage id={field} />
                    </Grid>
                    <Grid size={8}>{row.substitution[field]}</Grid>
                </Fragment>,
            );
        }
    }
    return rows.length ? (
        <Grid container spacing={2}>
            {...rows}
        </Grid>
    ) : null;
};

const ClientView: React.FC = () => {
    const renderers = client.renderers?.read;
    const resourceName = "client";
    const fieldNameKeyFactory = (fieldName: string) => `${resourceName}.${fieldName}`;
    const data = useLoaderData() as any;
    console.log("loader data", data);

    let content: string | JSX.Element = "";

    function renderRuleTable() {
        const rows = [];
        for (const role of data.roles) {
            for (const rule of role["client_role_mapping_rules"]) {
                rows.push(
                    <TableRow key={rule.id}>
                        <TableCell>{rule.pattern}</TableCell>
                        <TableCell>{role.name}</TableCell>
                        <TableCell>
                            <ContextSubstitutions data={rule["context_substitutions"]} />
                        </TableCell>
                        <TableCell>
                            <RuleButton ruleId={rule.id} action="update" roles={data.roles} />{" "}
                            <DeleteResourceButton resourceName="client_role_mapping_rule" id={rule.id} />
                        </TableCell>
                    </TableRow>,
                );
            }
        }

        if (rows.length == 0) return null;

        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id="client_role_mapping_rule.pattern" defaultMessage="Pattern" />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id="client_role_mapping_rule.role" defaultMessage="$user_roles.role" />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage
                                id="client_role_mapping_rule.substitutions"
                                defaultMessage="Substitutions"
                            />
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>{...rows}</TableBody>
            </Table>
        );
    }

    function renderRoleMapperSection() {
        const table = renderRuleTable();

        return (
            <ViewSection
                icon={faIdCardAlt}
                title={defineMessage({
                    id: "client.roleRuleSettingsTitle",
                    defaultMessage: "Role mapping rules",
                })}
                button={<RuleButton action="create" roles={data.roles} />}
            >
                <div>
                    {table || (
                        <NotSetValue
                            label={defineMessage({
                                id: "client.no_rules",
                                defaultMessage: "There were no role mapping rules added yet.",
                            })}
                        />
                    )}
                </div>
            </ViewSection>
        );
    }

    function renderRolesSection() {
        const rows = [];

        for (const role of data.roles) {
            rows.push(
                <TableRow key={role.id}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>
                        {role.allowed_networks?.length > 0 ? role.allowed_networks.join(", ") : <NotSetValue />}
                    </TableCell>
                    <TableCell>
                        {role.mfa_required ? (
                            <FormattedMessage id="yes" defaultMessage="Yes" />
                        ) : (
                            <FormattedMessage id="no" defaultMessage="No" />
                        )}
                    </TableCell>
                    <TableCell>
                        <ResourceFormButton
                            resourceName="role"
                            action="update"
                            dialogTitle={defineMessage({
                                id: "role.update",
                                defaultMessage: "Update role",
                            })}
                            id={role.id}
                            buttonType="icon"
                            formFields={["allowed_networks", "mfa_required"]}
                        />
                    </TableCell>
                </TableRow>,
            );
        }

        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id="role.name" defaultMessage="$name" />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id="role.allowed_networks" defaultMessage="Allowed networks" />
                        </TableCell>
                        <TableCell rowSpan={2}>
                            <FormattedMessage id="role.mfa_required" defaultMessage="MFA required" />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
            </Table>
        );
    }

    function renderSections() {
        // const basicDataFields = ["identifier", "secret", "refresh_token_expires_in", "redirect_uris"];
        const basicDataFields: DataField[] = [
            {
                name: "identifier",
                label: defineMessage({
                    id: "client.identifier",
                    defaultMessage: "Identifier",
                }),
            },
            {
                name: "secret",
                label: defineMessage({
                    id: "client.secret",
                    defaultMessage: "Secret",
                }),
            },
            {
                name: "refresh_token_expires_in",
                label: defineMessage({
                    id: "client.refresh_token_expires_in",
                    defaultMessage: "Refresh token expires in",
                }),
            },
            {
                name: "redirect_uris",
                label: defineMessage({
                    id: "client.redirect_uris",
                    defaultMessage: "Redirect URI(s)",
                }),
            },
            {
                name: "token_endpoint_auth_method",
                label: defineMessage({
                    id: "client.token_endpoint_auth_method",
                    defaultMessage: "Token endpoint authentication",
                }),
            },
            {
                name: "grant_types",
                label: defineMessage({
                    id: "client.grant_types",
                    defaultMessage: "Grant types",
                }),
            },
            {
                name: "response_types",
                label: defineMessage({
                    id: "client.response_types",
                    defaultMessage: "Response types",
                }),
            },
            apiAccessField,
        ];

        if (window.features.ldap) {
            basicDataFields.push({
                name: "ldap_auth",
                label: defineMessage({
                    id: "client.ldap_auth",
                    defaultMessage: "LDAP",
                }),
            });
        }

        return (
            <Stack spacing={5} mt={5}>
                <ViewSection
                    icon={faCube}
                    title={userTranslations.user.basic_data}
                    button={
                        <ResourceFormButton
                            resourceName="client"
                            action="update"
                            dialogTitle={defineMessage({
                                id: "client.update",
                                defaultMessage: "Update client",
                            })}
                            id={data.id}
                            buttonType="section-icon"
                            formFields={
                                isInternalClient(data.id) ? ["display_name", "refresh_token_expires_in"] : undefined
                            }
                        />
                    }
                >
                    <BasicTable
                        data={data}
                        fields={basicDataFields}
                        renderers={renderers}
                        fieldNameKeyFactory={fieldNameKeyFactory}
                    />
                </ViewSection>
                <ViewSection
                    icon={faKey}
                    title={defineMessage({
                        id: "client.roles",
                        defaultMessage: "$users.roles",
                    })}
                >
                    {renderRolesSection()}
                </ViewSection>
                {data["ldap_auth"] && renderRoleMapperSection()}
            </Stack>
        );
    }

    if (global.identityProvider == "internal") {
        content = renderSections();
    } else {
        content = (
            <BasicTable
                data={data}
                fields={[
                    {
                        name: "secret",
                        label: defineMessage({
                            id: "client.secret",
                            defaultMessage: "Secret",
                        }),
                    },
                    apiAccessField,
                ]}
                renderers={renderers}
                fieldNameKeyFactory={fieldNameKeyFactory}
            />
        );
    }

    return <TitledContent title={data["display_name"]}>{content}</TitledContent>;
};

export default ClientView;
